import React, { useEffect, useState } from 'react';
import './App.css';
import Projects from './components/Projects/Projects';
import ProjectModal from './components/ProjectModal/ProjectModal';
import LearningsPredictions from './components/LearningsPredictions/LearningsPredictions';
import profileImage from './eu.png';
import Loading from './components/Loading/Loading';
import ProfessionalProfile from './components/ProfessionalProfile/ProfessionalProfile';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Footer from './components/Footer/Footer';
import { sanitizeProjects } from './utils/sanitizeProjectData';

function App() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIdentity, setActiveIdentity] = useState('personal');
  const [projectsError, setProjectsError] = useState('');

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setIsLoading(true);
        setProjectsError('');

        const [response] = await Promise.all([
          fetch('/data/projects.json', {
            headers: {
              Accept: 'application/json',
            },
          }),
          new Promise((resolve) => setTimeout(resolve, 350)),
        ]);

        if (!response.ok) {
          throw new Error(`Project data request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data?.projects)) {
          throw new Error('Project data is malformed.');
        }

        setProjects(sanitizeProjects(data.projects));
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects([]);
        setProjectsError('Project data is temporarily unavailable. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, []);

  const openProjectModal = (project) => setSelectedProject(project);
  const closeProjectModal = () => setSelectedProject(null);

  useEffect(() => {
    document.body.style.overflow = selectedProject ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedProject]);

  const totalSkills = new Set(projects.flatMap((project) => project.skills || [])).size;
  const totalMedia = projects.reduce((count, project) => {
    const media = project.media || {};
    return count + (media.images?.length || 0) + (media.videos?.length || 0) + (media.audio?.length || 0);
  }, 0);

  return (
    <div className="App">
      {isLoading && <Loading />}
      <ScrollToTop />

      <div className="app-background" aria-hidden="true">
        <div className="paper-noise" />
      </div>

      <div className="app-shell">
        <header className="hero" id="top">
          <div className="hero-copy">
            <span className="hero-kicker">Portfolio</span>
            <h1>Software, media and long-term projects.</h1>
            <p className="hero-lead">
              I work across frontend, fullstack and experimental technology,
              with a portfolio that moves between professional product work and
              long-term personal projects.
            </p>

            <div className="hero-actions">
              <button
                type="button"
                className="hero-button hero-button--primary"
                onClick={() => {
                  setActiveIdentity('personal');
                  window.location.hash = 'identity';
                }}
              >
                View Projects
              </button>
              <a
                href="/cv.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-button hero-button--ghost"
              >
                Open CV
              </a>
            </div>
          </div>

          <aside className="hero-aside" aria-label="Profile panels">
            <section className="profile-panel">
              <div className="profile-panel__image-wrap">
                <img src={profileImage} alt="Alexandre Nunes" className="profile-image" />
              </div>

              <div className="profile-panel__content">
                <span className="profile-panel__eyebrow">Alexandre Nunes</span>
                <p className="profile-panel__quote">
                  Technology and creativity, treated as one practice.
                </p>
                <div className="social-icons">
                  <a
                    href="https://www.linkedin.com/in/alexandrenunesdeveloper/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <img src="/linkedin.png" alt="LinkedIn" />
                  </a>
                  <a
                    href="https://github.com/alexnunes2015"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                  >
                    <img src="/github.png" alt="GitHub" />
                  </a>
                </div>
              </div>
            </section>
          </aside>
        </header>

        <main className="main-content">
          <section className="identity-stage" id="identity" aria-label="Professional and personal profile">
            <div className="identity-stage__header">
              <span className="section-kicker">Two Sides</span>
              <div className="identity-stage__topline">
                <h2 className="section-title">Professional work and personal work.</h2>
                <div className="identity-switcher" aria-label="Profile view switcher">
                  <button
                    type="button"
                    className={`identity-trigger ${activeIdentity === 'professional' ? 'is-active' : ''}`}
                    onClick={() => setActiveIdentity('professional')}
                    aria-pressed={activeIdentity === 'professional'}
                  >
                    Professional Self
                  </button>

                  <button
                    type="button"
                    className={`identity-trigger ${activeIdentity === 'personal' ? 'is-active' : ''}`}
                    onClick={() => setActiveIdentity('personal')}
                    aria-pressed={activeIdentity === 'personal'}
                  >
                    Personal Self
                  </button>
                </div>
              </div>
              <p className="section-description">
                Switch between the two views.
              </p>
            </div>

            <div className="identity-stage__content">
              {activeIdentity === 'professional' ? (
                <ProfessionalProfile />
              ) : (
                <div className="identity-panel__content">
                  <Projects
                    projects={projects}
                    openProjectModal={openProjectModal}
                    projectsError={projectsError}
                    isLoading={isLoading}
                  />

                  <LearningsPredictions />
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeProjectModal} />
      )}

      <div className="app-shell">
        <Footer
          projectCount={projects.length}
          totalSkills={totalSkills}
          totalMedia={totalMedia}
        />
      </div>
    </div>
  );
}

export default App;
